import { Navigation } from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar, Users, MapPin, Clock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';

const DigitalDetox = () => {
  const navigate = useNavigate();

  const handleBooking = () => {
    navigate('/');
    setTimeout(() => window.scrollTo(0, 0), 100);
  };

  const handleGoBack = () => {
    navigate('/#upcoming-experiences');
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Event",
    "name": "Digital Detox Retreat",
    "description": "Rediscover life beyond the screen in nature's embrace. 3-day complete break from digital devices in an area with no cell service.",
    "startDate": "2024-05-03",
    "endDate": "2024-05-05",
    "location": {
      "@type": "Place",
      "name": "Offline Forest Sanctuary",
      "address": "Beskid Wyspowy, Poland"
    },
    "organizer": {
      "@type": "Organization",
      "name": "Mystic Forest",
      "url": "https://mysticforest.club"
    },
    "offers": {
      "@type": "Offer",
      "price": "425",
      "priceCurrency": "USD",
      "availability": "https://schema.org/InStock"
    }
  };

  return (
    <>
      <SEO
        title="Digital Detox Retreat - Disconnect to Reconnect with Nature"
        description="3-day complete digital detox in nature. Break free from screens and notifications to rediscover presence, peace, and authentic connection."
        canonicalUrl="https://mysticforest.club/experiences/digital-detox"
        jsonLd={jsonLd}
      />
      
      <div className="min-h-screen bg-background">
        <Navigation />
      
      {/* Go Back Button */}
      <div className="pt-20 pb-4">
        <div className="max-w-6xl mx-auto px-6">
          <Button 
            variant="ghost" 
            onClick={handleGoBack}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Experiences
          </Button>
        </div>
      </div>
      
      {/* Hero Section */}
      <section className="relative py-32">
        <div className="absolute inset-0">
          <img 
            src="/lovable-uploads/6da5d717-5ebd-461a-bb63-d2d4bc13c505.png" 
            alt="Person finding peace in forest during digital detox retreat"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        
        <div className="relative max-w-4xl mx-auto px-6 text-center text-white">
          <h1 className="text-5xl md:text-6xl font-serif font-light mb-6">
            Digital Detox Retreat
          </h1>
          <p className="text-xl md:text-2xl mb-8 opacity-90">
            Rediscover life beyond the screen in nature's embrace
          </p>
          <Button size="lg" className="px-8 py-4 text-lg" onClick={handleBooking}>
            Disconnect to Reconnect
          </Button>
        </div>
      </section>

      {/* Quick Info */}
      <section className="py-16 bg-card">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div className="flex flex-col items-center">
              <Calendar className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">When</h3>
              <p className="text-muted-foreground">May 3-5, 2024</p>
            </div>
            <div className="flex flex-col items-center">
              <MapPin className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Where</h3>
              <p className="text-muted-foreground">Offline Forest Sanctuary</p>
            </div>
            <div className="flex flex-col items-center">
              <Users className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">For Whom</h3>
              <p className="text-muted-foreground">Screen-weary souls</p>
            </div>
            <div className="flex flex-col items-center">
              <Clock className="w-8 h-8 mb-4 text-accent" />
              <h3 className="font-semibold mb-2">Duration</h3>
              <p className="text-muted-foreground">3 Days, 2 Nights</p>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-8 text-center">
            Remember What Life Feels Like
          </h2>
          <div className="prose prose-lg max-w-none text-muted-foreground">
            <p className="mb-6">
              When was the last time you spent a day without checking your phone? In our hyper-connected world, we've forgotten the profound peace that comes from simply being present with ourselves and nature.
            </p>
            <p className="mb-6">
              Our Digital Detox Retreat offers a complete break from screens, notifications, and digital overwhelm. Located in an area with no cell service, you'll have no choice but to reconnect with the analog world – and yourself.
            </p>
            <p className="mb-6">
              This isn't about punishment or deprivation; it's about rediscovering the richness of direct experience and learning sustainable practices for managing technology in your daily life.
            </p>
          </div>
        </div>
      </section>

      {/* The Process */}
      <section className="py-16 bg-card">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-12 text-center">
            The Detox Process
          </h2>
          <div className="space-y-8">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-sm font-semibold">1</span>
                  The Great Surrender
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Upon arrival, all devices are lovingly stored in our "digital locker." Feel the immediate shift as you step into presence.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-sm font-semibold">2</span>
                  Analog Adventures
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="list-disc list-inside text-muted-foreground space-y-2">
                  <li>Hand-written journaling by candlelight</li>
                  <li>Book reading in forest hammocks</li>
                  <li>Board games and deep conversations</li>
                  <li>Star gazing without photographing</li>
                  <li>Cooking together without timers</li>
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <span className="w-8 h-8 bg-accent text-accent-foreground rounded-full flex items-center justify-center text-sm font-semibold">3</span>
                  Mindful Re-entry
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Learn sustainable practices for maintaining digital wellness. Create personal boundaries and rituals for healthy tech use.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* What You'll Discover */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-serif font-light mb-12 text-center">
            What You'll Rediscover
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <Card>
              <CardHeader>
                <CardTitle>Deep Sleep</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Experience natural sleep cycles without blue light disruption. Wake refreshed and energized.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Focused Attention</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Regain your ability to focus deeply on single tasks without the urge to multitask or check devices.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Real Conversation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Engage in meaningful dialogue without distractions, building authentic connections with fellow participants.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Creative Flow</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Access your natural creativity through art, writing, and music without digital interference.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Investment */}
      <section className="py-16 bg-card">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-serif font-light mb-8">Investment</h2>
          <Card className="p-8">
            <div className="text-4xl font-semibold mb-4">$425</div>
            <p className="text-muted-foreground mb-6">Per person</p>
            <div className="text-sm text-muted-foreground mb-8">
              <p>Includes accommodation, all meals, activities</p>
              <p>Secure device storage and 24/7 emergency contact</p>
            </div>
            <Button size="lg" className="w-full" onClick={handleBooking}>
              Begin Your Detox
            </Button>
          </Card>
        </div>
      </section>

        <Footer />
      </div>
    </>
  );
};

export default DigitalDetox;